import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { take } from 'rxjs/operators';

import { parseTakeoutUpload } from '../../google-takeout';
import {
  GoogleReview,
  GoogleReviewAction,
  GoogleReviewImportSummary,
  GoogleReviewService,
} from '../../services/google-review.service';
import { ModalService } from '../../services/modal.service';

type StatusTab = 'pending' | 'approved' | 'suppressed' | 'removed' | 'all';
type SortKey = 'newest' | 'oldest' | 'best-first' | 'lowest';

interface ArrangerOption {
  label: string;
  value: number;
}

/**
 * Website → Google reviews (#173, GOOGLE-REVIEWS §5): the moderation queue.
 *
 * Bulk-first because the first pass is the whole backlog — a few hundred
 * reviews across 88 listings. Filter down to a lane ("5★ with text"), select
 * all matching, approve; or work row by row from the keyboard:
 *   ↑/↓ or k/j move · a approve · s suppress · p back to pending · x select
 *
 * Nothing is approved by default and nothing is ever auto-approved: review
 * text routinely names the family or the deceased. Approving only marks a
 * review as allowed on picaluna.com; it goes live with the next site publish.
 */
@Component({
  selector: 'app-content-review-list',
  templateUrl: './content-review-list.component.html',
  styleUrls: ['./content-review-list.component.scss'],
})
export class ContentReviewListComponent implements OnInit {
  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;
  @ViewChild('queue') queue?: ElementRef<HTMLElement>;

  readonly pageSize = 25;

  loading = true;
  loadError: string | null = null;
  reviews: GoogleReview[] = [];
  visible: GoogleReview[] = [];
  counts: Record<StatusTab, number> = { pending: 0, approved: 0, suppressed: 0, removed: 0, all: 0 };

  // Filters
  tab: StatusTab = 'pending';
  search = '';
  ratings: number[] = [];
  locationId: string | null = null;
  textOnly = false;
  unassignedOnly = false;
  sort: SortKey = 'newest';

  /** p-selectButton options — labels carry the live counts, rebuilt in refresh(). */
  tabOptions: { label: string; value: StatusTab }[] = [];

  readonly tabs: { key: StatusTab; label: string }[] = [
    { key: 'pending', label: 'To review' },
    { key: 'approved', label: 'Approved' },
    { key: 'suppressed', label: 'Suppressed' },
    { key: 'removed', label: 'Removed from Google' },
    { key: 'all', label: 'All' },
  ];
  readonly ratingOptions = [5, 4, 3, 2, 1].map((n) => ({ label: `${n} star${n === 1 ? '' : 's'}`, value: n }));
  readonly sortOptions: { label: string; value: SortKey }[] = [
    { label: 'Newest first', value: 'newest' },
    { label: 'Oldest first', value: 'oldest' },
    { label: '5★ with text first', value: 'best-first' },
    { label: 'Lowest rating first', value: 'lowest' },
  ];
  locationOptions: { label: string; value: string }[] = [];

  // Selection + keyboard focus
  selected = new Set<number>();
  focusIndex = 0;
  first = 0;
  expanded = new Set<number>();

  busy = false;
  importing = false;
  lastImport: GoogleReviewImportSummary | null = null;

  arrangerOptions: ArrangerOption[] = [];
  private arrangerNames = new Map<number, string>();
  /** Lower-cased first name → arranger ids sharing it (collisions are why hints are advisory). */
  private firstNames = new Map<string, number[]>();
  private mentionCache = new Map<number, ArrangerOption[]>();

  constructor(
    private reviewService: GoogleReviewService,
    private modalService: ModalService
  ) {}

  ngOnInit(): void {
    this.load();
    // Current staff, from the review route itself: GET /user can't tell
    // current staff from people who have left for a Content Manager (its rows
    // are narrowed to public-profile fields, #267), and it includes the
    // External API's system user.
    this.reviewService
      .arrangers()
      .pipe(take(1))
      .subscribe({
        next: (arrangers) => {
          const options: ArrangerOption[] = [];
          for (const a of arrangers || []) {
            const label = `${a.firstName} ${a.lastName}`.trim();
            if (!label) continue;
            options.push({ label, value: a.id });
            this.arrangerNames.set(a.id, label);
            const first = a.firstName.trim().toLowerCase();
            if (first.length >= 3) {
              this.firstNames.set(first, [...(this.firstNames.get(first) || []), a.id]);
            }
          }
          this.arrangerOptions = options.sort((a, b) => a.label.localeCompare(b.label));
          this.mentionCache.clear();
        },
        error: () => (this.arrangerOptions = []),
      });
  }

  load(): void {
    this.loading = true;
    this.loadError = null;
    this.reviewService
      .list()
      .pipe(take(1))
      .subscribe({
        next: (reviews) => {
          this.reviews = reviews;
          this.loading = false;
          this.mentionCache.clear();
          this.rebuildLocations();
          this.refresh();
          setTimeout(() => this.queue?.nativeElement.focus());
        },
        error: (err) => {
          this.loading = false;
          this.loadError = err?.error?.error?.title || err?.message || 'The reviews could not be loaded.';
        },
      });
  }

  // ---------------------------------------------------------------- filtering

  setTab(tab: StatusTab | null): void {
    // PrimeNG 12's p-selectButton has no allowEmpty: clicking the active option
    // emits null. Keep the current tab and re-assert it so the button re-selects.
    if (!tab) {
      const current = this.tab;
      this.tab = null as any;
      setTimeout(() => (this.tab = current));
      return;
    }
    this.tab = tab;
    this.selected.clear();
    this.focusIndex = 0;
    this.first = 0;
    this.refresh();
  }

  onFilterChange(): void {
    this.focusIndex = 0;
    this.first = 0;
    this.refresh();
  }

  clearFilters(): void {
    this.search = '';
    this.ratings = [];
    this.locationId = null;
    this.textOnly = false;
    this.unassignedOnly = false;
    this.onFilterChange();
  }

  get hasFilters(): boolean {
    return !!(this.search || this.ratings.length || this.locationId || this.textOnly || this.unassignedOnly);
  }

  /** Recompute counts + the visible list. Called after every load, filter change and write. */
  refresh(): void {
    const counts: Record<StatusTab, number> = { pending: 0, approved: 0, suppressed: 0, removed: 0, all: 0 };
    for (const r of this.reviews) {
      counts.all++;
      counts[this.tabOf(r)]++;
    }
    this.counts = counts;
    this.tabOptions = this.tabs.map((t) => ({ label: `${t.label} (${counts[t.key]})`, value: t.key }));

    const q = this.search.trim().toLowerCase();
    const list = this.reviews.filter((r) => {
      if (this.tab !== 'all' && this.tabOf(r) !== this.tab) return false;
      if (this.ratings.length && !this.ratings.includes(r.starRating ?? 0)) return false;
      if (this.locationId && r.locationId !== this.locationId) return false;
      if (this.textOnly && !r.comment) return false;
      if (this.unassignedOnly && r.arrangerUserIds.length) return false;
      if (q) {
        const hay = `${r.comment || ''} ${r.reviewerName || ''} ${r.locationName || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });

    const time = (r: GoogleReview) => r.createTime || '';
    const byNewest = (a: GoogleReview, b: GoogleReview) => time(b).localeCompare(time(a));
    const sorters: Record<SortKey, (a: GoogleReview, b: GoogleReview) => number> = {
      newest: byNewest,
      oldest: (a, b) => -byNewest(a, b),
      'best-first': (a, b) =>
        this.yield(b) - this.yield(a) || (b.starRating ?? 0) - (a.starRating ?? 0) || byNewest(a, b),
      lowest: (a, b) => (a.starRating ?? 0) - (b.starRating ?? 0) || byNewest(a, b),
    };
    this.visible = list.sort(sorters[this.sort]);

    // Selection only ever means "visible rows" — a hidden selected row would be
    // approved by a button the user can't see the effect of.
    const visibleIds = new Set(this.visible.map((r) => r.id));
    this.selected.forEach((id) => !visibleIds.has(id) && this.selected.delete(id));

    this.focusIndex = Math.min(this.focusIndex, Math.max(this.visible.length - 1, 0));
    this.keepFocusOnPage();
  }

  /** Approved and suppressed rows that left Google live under "Removed", not their status tab. */
  tabOf(r: GoogleReview): Exclude<StatusTab, 'all'> {
    return r.removedFromGoogleAt ? 'removed' : r.status;
  }

  /** The high-yield lane for a first pass: 5★ with text. */
  private yield(r: GoogleReview): number {
    return r.starRating === 5 && r.comment ? 1 : 0;
  }

  private rebuildLocations(): void {
    const names = new Map<string, string>();
    for (const r of this.reviews) {
      if (!names.has(r.locationId) || (r.locationName && names.get(r.locationId) === r.locationId)) {
        names.set(r.locationId, r.locationName || r.locationId);
      }
    }
    this.locationOptions = Array.from(names, ([value, label]) => ({ value, label })).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }

  // ---------------------------------------------------------------- selection

  isSelected(r: GoogleReview): boolean {
    return this.selected.has(r.id);
  }

  toggleSelected(r: GoogleReview): void {
    if (this.selected.has(r.id)) this.selected.delete(r.id);
    else this.selected.add(r.id);
  }

  selectAllVisible(): void {
    this.visible.forEach((r) => this.selected.add(r.id));
  }

  clearSelection(): void {
    this.selected.clear();
  }

  get allVisibleSelected(): boolean {
    return this.visible.length > 0 && this.visible.every((r) => this.selected.has(r.id));
  }

  // ---------------------------------------------------------------- moderation

  moderateSelected(action: GoogleReviewAction): void {
    this.moderate(Array.from(this.selected), action);
  }

  moderateOne(r: GoogleReview, action: GoogleReviewAction): void {
    this.focusIndex = Math.max(this.visible.indexOf(r), 0);
    this.moderate([r.id], action);
  }

  private moderate(ids: number[], action: GoogleReviewAction): void {
    if (!ids.length || this.busy) return;
    this.busy = true;
    const status = action === 'approve' ? 'approved' : action === 'suppress' ? 'suppressed' : 'pending';
    // take(1), not takeUntil: closing the dialog must not abort a write in flight.
    this.reviewService
      .moderate(ids, action)
      .pipe(take(1))
      .subscribe({
        next: () => {
          const set = new Set(ids);
          for (const r of this.reviews) {
            if (set.has(r.id)) {
              r.status = status;
              r.requeueReason = null;
            }
          }
          ids.forEach((id) => this.selected.delete(id));
          this.busy = false;
          this.refresh();
        },
        error: (err) => {
          this.busy = false;
          this.modalService.error(err?.error?.error || err);
        },
      });
  }

  // ---------------------------------------------------------------- arrangers

  onArrangersChange(r: GoogleReview, ids: number[]): void {
    const previous = r.arrangerUserIds;
    r.arrangerUserIds = ids;
    this.mentionCache.delete(r.id);
    this.reviewService
      .setArrangers(r.id, ids)
      .pipe(take(1))
      .subscribe({
        next: (saved) => (r.arrangerUserIds = saved.arrangerUserIds),
        error: (err) => {
          r.arrangerUserIds = previous;
          this.mentionCache.delete(r.id);
          this.modalService.error(err?.error?.error || err);
        },
      });
  }

  addArranger(r: GoogleReview, id: number): void {
    if (!r.arrangerUserIds.includes(id)) this.onArrangersChange(r, [...r.arrangerUserIds, id]);
  }

  /**
   * Arrangers whose first name appears as a word in the review — a HINT for a
   * human to confirm (GOOGLE-REVIEWS §7). Never assigned automatically:
   * attributing a family's words to the wrong person is the failure mode.
   */
  mentions(r: GoogleReview): ArrangerOption[] {
    const cached = this.mentionCache.get(r.id);
    if (cached) return cached;
    const found: ArrangerOption[] = [];
    if (r.comment && this.firstNames.size) {
      // Capitalised words only: names are capitalised, and lower-case prose
      // ("support", "grace", "hope") is not a mention of someone called that.
      const words = new Set((r.comment.match(/\b[A-ZÀ-Þ][a-zß-ɏ']+/g) || []).map((w) => w.toLowerCase()));
      words.forEach((w) => {
        for (const id of this.firstNames.get(w) || []) {
          if (!r.arrangerUserIds.includes(id)) found.push({ value: id, label: this.arrangerNames.get(id) || '' });
        }
      });
    }
    this.mentionCache.set(r.id, found);
    return found;
  }

  // ---------------------------------------------------------------- import

  chooseFile(): void {
    this.fileInput?.nativeElement.click();
  }

  async onFilesChosen(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    input.value = '';
    if (!files.length) return;

    this.importing = true;
    let parsed;
    try {
      parsed = await parseTakeoutUpload(files);
    } catch (e: any) {
      this.importing = false;
      this.modalService.generic({
        title: "That file couldn't be read",
        copy: ['Choose the Google Takeout .zip as downloaded, or a reviews.json file from inside it.'],
        buttons: [{ label: 'Close', key: 'close', class: 'p-button-text' }],
      });
      return;
    }

    if (!parsed.reviews.length) {
      this.importing = false;
      this.modalService.generic({
        title: 'No reviews found',
        copy: [
          `The file had ${parsed.folders} listing folder${parsed.folders === 1 ? '' : 's'} but no reviews.`,
          'Check the export included "Google Business Profile" and was made from the account that manages the listings.',
        ],
        buttons: [{ label: 'Close', key: 'close', class: 'p-button-text' }],
      });
      return;
    }

    this.reviewService
      .import(parsed.reviews, parsed.locationIds)
      .pipe(take(1))
      .subscribe({
        next: (summary) => {
          this.importing = false;
          this.lastImport = summary;
          this.load();
        },
        error: (err) => {
          this.importing = false;
          this.modalService.error(err?.error?.error || err);
        },
      });
  }

  // ---------------------------------------------------------------- keyboard

  get focusedId(): number | null {
    return this.visible[this.focusIndex]?.id ?? null;
  }

  focusRow(r: GoogleReview): void {
    this.focusIndex = Math.max(this.visible.indexOf(r), 0);
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLElement;
    if (target?.closest('input, textarea, select, .p-multiselect, .p-dropdown, .p-selectbutton, [contenteditable]')) return;
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    const row = this.visible[this.focusIndex];

    switch (event.key) {
      case 'ArrowDown':
      case 'j':
        this.moveFocus(1);
        break;
      case 'ArrowUp':
      case 'k':
        this.moveFocus(-1);
        break;
      case 'a':
        if (row && row.status !== 'approved') this.moderate([row.id], 'approve');
        break;
      case 's':
        if (row && row.status !== 'suppressed') this.moderate([row.id], 'suppress');
        break;
      case 'p':
        if (row && row.status !== 'pending') this.moderate([row.id], 'unapprove');
        break;
      case 'x':
        if (row) this.toggleSelected(row);
        break;
      default:
        return;
    }
    event.preventDefault();
  }

  private moveFocus(delta: number): void {
    this.focusIndex = Math.min(Math.max(this.focusIndex + delta, 0), Math.max(this.visible.length - 1, 0));
    this.keepFocusOnPage();
    setTimeout(() =>
      this.queue?.nativeElement.querySelector('.review-row-focused')?.scrollIntoView({ block: 'nearest' })
    );
  }

  private keepFocusOnPage(): void {
    this.first = Math.floor(this.focusIndex / this.pageSize) * this.pageSize;
  }

  // ---------------------------------------------------------------- display

  toggleExpanded(r: GoogleReview): void {
    if (this.expanded.has(r.id)) this.expanded.delete(r.id);
    else this.expanded.add(r.id);
  }

  trackById(_: number, r: GoogleReview): number {
    return r.id;
  }
}
